<template>
  <div>
    <v-chart :option="chartOption" :autoresize="true" @click="handleChartClick" />
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
]);

export default defineComponent({
  name: 'CreditGraphComponent',
  components: {
    VChart,
  },
  props: {
    asks: {
      type: Array,
      required: true,
    },
    bondType: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:maturityRange'],
  setup(props, { emit }) {
    const RANGES = [
      { min: 0, max: 3, label: '0-3M' },
      { min: 4, max: 6, label: '4-6M' },
      { min: 7, max: 9, label: '7-9M' },
      { min: 10, max: 12, label: '10-12M' },
      { min: 13, max: 18, label: '13-18M' },
      { min: 19, max: 24, label: '19-24M' },
      { min: 25, max: 36, label: '25-36M' },
      { min: 37, max: 60, label: '37-60M' },
      { min: 61, max: Infinity, label: '60M 이상' },
    ];

    // 차트 클릭 핸들러
    const handleChartClick = params => {
      if (params.componentType === 'series') {
        const clickedRange = RANGES.find(range => range.label === params.name);
        if (clickedRange) {
          if (clickedRange.min === 61) {
            emit('update:maturityRange', [61, 61]);
          } else {
            emit('update:maturityRange', [clickedRange.min, clickedRange.max]);
          }
        }
      }
    };

    const processData = computed(() => {
      if (!props.asks || props.asks.length === 0) {
        return RANGES.map(range => ({
          range: range.label,
          totalCount: 0,
          executedCount: 0,
          executionStrength: 0,
        }));
      }

      const today = new Date();
      const intervals = RANGES.map(range => ({
        range: range.label,
        min: range.min,
        max: range.max,
        totalCount: 0,
        executedCount: 0,
        executionStrength: 0,
      }));

      props.asks.forEach(ask => {
        if (!ask.maturityDate) return;

        const maturityDate = new Date(ask.maturityDate);
        const monthsDiff =
          (maturityDate.getFullYear() - today.getFullYear()) * 12 +
          (maturityDate.getMonth() - today.getMonth());

        if (monthsDiff >= 0) {
          const interval = intervals.find(
            int => monthsDiff >= int.min && monthsDiff <= int.max
          );

          if (interval) {
            interval.totalCount++;
            if (ask.hasTraded === true) {
              interval.executedCount++;
            }
          }
        }
      });

      intervals.forEach(interval => {
        interval.executionStrength =
          interval.totalCount > 0
            ? parseFloat(
              ((interval.executedCount / interval.totalCount) * 100).toFixed(
                1
              )
            )
            : 0;
      });

      return intervals;
    });

    const chartOption = computed(() => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: params => {
          const range = params[0].axisValue;
          let result = `<div class="font-bold">${range}</div>`;
          params.forEach(param => {
            const value =
              param.seriesName === '체결강도' ? `${param.value}%` : param.value;
            result += `<div>${param.marker} ${param.seriesName}: ${value}</div>`;
          });
          return result;
        },
      },
      legend: {
        data: ['전체 호가', '체결 호가', '체결강도'],
        bottom: '0',
        padding: [20, 10],
      },
      grid: {
        top: '5%',
        left: '3%',
        right: '5%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: processData.value.map(item => item.range),
        // name: '잔존 만기',
        nameLocation: 'middle',
        nameGap: 35,
        axisLabel: {
          interval: 0,
          rotate: 30,
          fontSize: 10,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: '호가 수(건)',
          nameTextStyle: {
            padding: [0, 30, 0, 0],
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#E5E7EB',
            },
          },
          axisLabel: {
            formatter: value => {
              if (props.asks.length === 0) return '';
              return `${value}건`;
            },
          },
        },
        {
          type: 'value',
          name: '체결강도(%)',
          nameTextStyle: {
            padding: [0, 0, 0, 30],
          },
          min: 0,
          max: 100,
          splitLine: {
            show: false,
          },
          axisLabel: {
            formatter: '{value}%',
          },
        },
      ],
      series: [
        {
          name: '전체 호가',
          type: 'bar',
          barMaxWidth: 30,
          data: processData.value.map(item => item.totalCount),
          yAxisIndex: 0,
          itemStyle: {
            color: '#818CF8',
            opacity: 0.8,
          },
        },
        {
          name: '체결 호가',
          type: 'bar',
          barMaxWidth: 30,
          data: processData.value.map(item => item.executedCount),
          yAxisIndex: 0,
          itemStyle: {
            color: '#34D399',
            opacity: 0.8,
          },
        },
        {
          name: '체결강도',
          type: 'line',
          yAxisIndex: 1,
          data: processData.value.map(item => item.executionStrength),
          itemStyle: {
            color: '#F97316',
          },
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2,
          },
        },
      ],
    }));

    return {
      chartOption,
      handleChartClick,
    };
  },
});
</script>
